"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";

type TestimonialImage = {
  id: number;
  src: string;
  alt: string;
};

interface TestimonialsCarouselProps {
  delay: number;
}

const FALLBACK_DELAY = 3000;

export default function TestimonialsCarousel({ delay }: TestimonialsCarouselProps) {
  const [images, setImages] = useState<TestimonialImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const effectiveDelay = useMemo(() => {
    return Number.isFinite(delay) && delay > 0 ? delay : FALLBACK_DELAY;
  }, [delay]);

  useEffect(() => {
    let isMounted = true;

    async function loadTestimonials() {
      try {
        const response = await fetch("/api/testimonials");

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (isMounted && Array.isArray(data?.items)) {
          setImages(data.items);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error("Failed to load testimonials", error);
      }
    }

    loadTestimonials();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (images.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, effectiveDelay);

    return () => clearInterval(interval);
  }, [images, effectiveDelay]);

  if (images.length === 0) {
    return null;
  }

  const activeImage = images[currentIndex];

  return (
    <div className="mt-10 flex justify-center">
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-emerald-200 bg-white/80 p-4 shadow-lg backdrop-blur">
        <div className="relative aspect-square w-full">
          <Image
            key={activeImage.id}
            alt={activeImage.alt}
            className="h-full w-full rounded-2xl object-cover transition-opacity duration-700"
            fill
            src={activeImage.src}
            sizes="(min-width: 1024px) 320px, 80vw"
            priority
          />
        </div>
      </div>
    </div>
  );
}
